/**
 * Created by rickborst on 24/04/2017.
 */

import _ from 'lodash';

import ErrorCodes from 'errorcodes';
import {getInstance as getDataSources} from 'datasources';
import {attemptLogout} from 'store/actions/modals/login';
import {hashHistory} from 'react-router';
import {getInstance as getStore} from 'store/store';
import {setPlayer} from 'store/actions/widgets/player';

const ViewTreeActionTypes = {
        SET_VIEW: 'SET_VIEW',
        REMOVE_VIEW: 'REMOVE_VIEW',
        SET_ACTIVE_VIEW: 'SET_ACTIVE_VIEW',
        SET_ACTIVE_VIEW_STATUS: 'SET_ACTIVE_VIEW_STATUS',
        SET_VIEW_BACKGROUND: 'SET_VIEW_BACKGROUND',
        SET_RAIL: 'SET_RAIL',
        SET_REMINDER: 'SET_REMINDER',
        SET_JUMBO_REMINDER: 'SET_JUMBO_REMINDER',
        SET_LOAD_BLOCKER_STATUS: 'SET_LOAD_BLOCKER_STATUS'
    },

    ViewStatuses = {
        UNPREPARED: 'unprepared',
        PREPARED: 'prepared'
    },

    LoadBlockerStatuses = {
        BLOCKING: 'blocking',
        DO_NOT_STORE: 'do-not-store'
    },

    ActiveViewStatuses = {
        LOADING: 'loading',
        READY: 'ready',
        ERROR: 'error'
    },

    /**
     * Sets a view within the state.
     *
     * @param {Object} view - View object.
     * @returns {Object} Action.
     */
    setView = function (view) {

        return {
            type: ViewTreeActionTypes.SET_VIEW,
            view
        };
    },

    /**
     * Removes a view from the state.
     *
     * @param {Object} view - View object.
     * @returns {Object} Action.
     */
    removeView = function (view) {

        return {
            type: ViewTreeActionTypes.REMOVE_VIEW,
            view
        };
    },

    /**
     * Sets the active view.
     *
     * @param {Object} view - View object.
     * @returns {Object} Action.
     */
    setActiveView = function (view) {
        return {
            type: ViewTreeActionTypes.SET_ACTIVE_VIEW,
            view
        };
    },

    /**
     * Sets the active view status.
     *
     * @param {string} status - Active view status.
     * @returns {Object} Action.
     */
    setActiveViewStatus = function (status) {

        return {
            type: ViewTreeActionTypes.SET_ACTIVE_VIEW_STATUS,
            status
        };
    },

    /**
     * Sets load blocker status.
     *
     * @param {string} status - Active view status.
     * @returns {Object} Action.
     */
    setLoadBlockerStatus = function (status) {
        return {
            type: ViewTreeActionTypes.SET_LOAD_BLOCKER_STATUS,
            readyStatus: status
        };
    },

    /**
     * Loads a view and adds it to the state.
     *
     * @param {string} name - View name.
     * @param {Object} params - Params object.
     * @returns {Function} Function.
     */
    getViewByName = function (name, params) {
        let dataSources = getDataSources();

        return (dispatch, getState) => {

            let loadBlockerStatus = _.get(getState(), ['viewTree', 'loadBlockerStatus']),
                imStoring = false;

            if (loadBlockerStatus === LoadBlockerStatuses.BLOCKING) {

                // prevent storing previous state!
                dispatch(setLoadBlockerStatus(LoadBlockerStatuses.DO_NOT_STORE));
                imStoring = true;
            } else {
                dispatch(setLoadBlockerStatus(LoadBlockerStatuses.BLOCKING));
            }

            dataSources.getViewByName(name, params)
                .then((view) => {
                    let newLoadBlockerStatus = _.get(getState(), ['viewTree', 'loadBlockerStatus']);

                    if (newLoadBlockerStatus !== LoadBlockerStatuses.DO_NOT_STORE || imStoring) {
                        dispatch(setActiveView(view));
                        dispatch(setActiveViewStatus(ActiveViewStatuses.READY));
                    }
                }).catch(() => {
                    dispatch(setActiveViewStatus(ActiveViewStatuses.ERROR));
                    hashHistory.replace('/');
            });
        };
    },

    /**
     * Sets the active view background.
     *
     * @param {Object} background - Background image.
     * @returns {Object} Action.
     */
    setViewBackground = function (background) {

        return {
            type: ViewTreeActionTypes.SET_VIEW_BACKGROUND,
            background
        };
    },

    /**
     * Loads a background image based on the current view
     * and sets it on the state.
     *
     * @param {Object} view - View object.
     * @returns {Function} Function.
     */
    getViewBackground = function (view) {
        let dataSources = getDataSources();

        return dispatch => {

            dataSources.getViewBackground(view)
                .then(background => {
                    dispatch(setViewBackground(background));
                });
        };
    },

    setRail = function (rail) {

        return {
            type: ViewTreeActionTypes.SET_RAIL,
            rail
        };
    },

    /**
     * ClosePlayer.
     */
    closePlayer = () => {
        let store = getStore();

        store.dispatch(setPlayer({
            on: false,
            asset: {}
        }));
    },

    /**
     * Creates and returns a load initial rail assets action.
     *
     * @param {{rail: *, initialPagesAmount: *, pageSize: *}} params - Load params.
     * @returns {Function} Function that returns a promise
     * resolving to response to the asset list call.
     */
    loadInitialRailAssets = function (params) {
        return (dispatch, getState) => {  // eslint-disable-line no-unused-vars
            let dataSources = getDataSources(),
                requestParams = {
                    rail: params.rail,
                    pageSize: params.pageSize * params.initialPagesAmount,
                    pageIndex: 1
                };

            return dataSources.loadRailAssets(requestParams)
                .then((data) => {

                    let {rail, pageSize, loadReminders} = params,
                        {totalCount, assets} = data,
                        promisesArray = [];

                    rail.pagination = {
                        totalCount,
                        pageSize,
                        pageIndex: params.initialPagesAmount
                    };

                    if (loadReminders) {
                        _.forEach(assets, (asset) => {
                            promisesArray.push(dataSources.getReminder(asset.id).then((reminder) => {
                                asset.reminder = reminder;
                            }).catch(() => {}));
                        });
                    }

                    Promise.all(promisesArray).then(() => {
                        rail.assets = _.merge(rail.assets, assets);
                        dispatch(setRail(rail));
                    });
                }).catch((error) => {

                    // logout user when ks expires
                    let errorCode = _.get(error, ['result', 'error', 'code']);

                    if (errorCode === ErrorCodes.SESSION_EXPIRED) {
                        closePlayer();
                        dispatch(attemptLogout(true));
                    }

                    return Promise.reject(error);
                });
        };
    },

    /**
     * Creates and returns a load more assets action.
     *
     * @param {{rail: *, pageIndex: *, pageSize: *}} params - Load params.
     * @returns {Function} Function that returns a promise
     * resolving to response to the asset list call.
     */
    loadMoreAssets = function (params) {

        return (dispatch, getState) => {  // eslint-disable-line no-unused-vars

            let pagination = _.get(params, ['rail', 'pagination']),
                {
                    totalCount : previousTotalCount,
                    pageSize ,
                    pageIndex : previousPageIndex
                } = pagination,
                dataSources = getDataSources(),
                newPageIndex = previousPageIndex + 1,
                requestParams = {
                    rail: params.rail,
                    pageSize: pageSize,
                    pageIndex: newPageIndex
                },
                canLoadMore = () => {
                    return (previousTotalCount - previousPageIndex * pageSize) > 0;
                };


            if (!canLoadMore()) {
                return;
            }

            return dataSources.loadRailAssets(requestParams)
                .then((data) => {

                    let {rail} = params,
                        {totalCount, assets} = data;

                    rail.pagination = {
                        totalCount,
                        pageSize,
                        pageIndex: newPageIndex
                    };

                    rail.assets = rail.assets.concat(assets);

                    dispatch(setRail(rail));
                });
        };
    },

    /**
     * Sets current collection page asset bookmark.
     *
     * @param {Object} reminder - Reminder.
     * @returns {Function} Function that returns a promise resolving to response to the asset list call.
     */
    setCollectionAssetReminder = function (reminder) {
        return {
            type: ViewTreeActionTypes.SET_REMINDER,
            reminder: reminder
        };
    },

    /**
     * Sets current JUMBO asset bookmark.
     *
     * @param {Object} reminder - Reminder.
     * @param {number} assetId - Asset.
     * @returns {Function} Function that returns a promise resolving to response to the asset list call.
     */
    setJumboAssetReminder = function (reminder, assetId) {
        return {
            type: ViewTreeActionTypes.SET_JUMBO_REMINDER,
            reminder: reminder,
            assetId: assetId
        };
    };


export {
    ViewTreeActionTypes,
    ViewStatuses,
    ActiveViewStatuses,
    setView,
    removeView,
    setActiveView,
    setActiveViewStatus,
    getViewByName,
    getViewBackground,
    loadInitialRailAssets,
    loadMoreAssets,
    setCollectionAssetReminder,
    setJumboAssetReminder
};


// WEBPACK FOOTER //
// ./src/js/store/actions/viewtree.js